import React, { forwardRef, useEffect, useRef, useState } from 'react';

import type { ChatMessage } from '../../services/types';
import { useFileSizeFormatter } from '../../utils/formatters';

import { ImageModal } from './ImageModal';
import { renderMessageText } from './renderMessageText';

interface ChatPanelProps {
  messages: ChatMessage[];
}

export const ChatPanel = forwardRef<HTMLDivElement, ChatPanelProps>(
  ({ messages }, ref) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const [modalImage, setModalImage] = useState<{ url: string; alt: string } | null>(null);
    const formatFileSize = useFileSizeFormatter();

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
      const panel = (ref as React.RefObject<HTMLDivElement>)?.current || panelRef.current;
      if (!panel) return;

      // Reference messages.length so effect re-runs when messages change
      const messageCount = messages.length;
      if (messageCount === 0) return;

      // Check if user is already near the bottom before scrolling
      const isNearBottom = panel.scrollTop >= panel.scrollHeight - panel.clientHeight - 50;
      
      if (isNearBottom) {
        panel.scrollTop = panel.scrollHeight;
      }
    }, [messages, ref]);

    const handleImageClick = (imageUrl: string, alt: string) => {
      setModalImage({ url: imageUrl, alt });
    };

    const handleCloseModal = () => {
      setModalImage(null);
    };

    const handleImageKeyDown = (event: React.KeyboardEvent, imageUrl: string, alt: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleImageClick(imageUrl, alt);
      }
    };

    const renderMessage = (message: ChatMessage, index: number) => {
      const isBot = message.from === 'bot';
      const messageClasses = `chat-message ${isBot ? 'bot-message' : 'user-message'}`;

      return (
        <div key={index} className={messageClasses}>
          <div className="message-content">
            {/* Loading indicator */}
            {message.isLoading && (
              <div className="loading-indicator">
                <span className="loading-dots">...</span>
              </div>
            )}

            {/* Image analyzing indicator */}
            {message.isAnalyzingImage && (
              <div className="analyzing-indicator">
                {__('Analyzing image...')}
              </div>
            )}

            {/* Message text */}
            {message.text && (
              <div className="message-text">
                {isBot ? renderMessageText(message.text) : message.text}
              </div>
            )}

            {/* Image attachment */}
            {message.imageUrl && (
              <div className="message-image">
                <img
                  src={message.imageUrl}
                  alt={message.fileName ? `${__('Attachment')}: ${message.fileName}` : __('Uploaded image attachment')}
                  onClick={() => handleImageClick(message.imageUrl!, message.fileName || __('Uploaded image'))}
                  onKeyDown={(e) => handleImageKeyDown(e, message.imageUrl!, message.fileName || __('Uploaded image'))}
                  role="button"
                  tabIndex={0}
                  aria-label={__('Click to enlarge image')}
                  style={{ cursor: 'pointer', maxWidth: '200px', borderRadius: '8px' }}
                />
                {message.fileName && (
                  <div className="image-info">
                    <span className="file-name">{message.fileName}</span>
                    {message.fileSize && (
                      <span className="file-size">({formatFileSize(message.fileSize)})</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <>
        <div 
          className="chat-panel" 
          ref={(ref as React.RefObject<HTMLDivElement>) || panelRef}
          role="log"
          aria-label={__('Chat conversation')}
          aria-live="polite"
        >
          <div className="messages-container">
            {messages.map((message, index) => renderMessage(message, index))}
          </div>
        </div>
        
        {modalImage && (
          <ImageModal
            isOpen={!!modalImage}
            imageUrl={modalImage.url}
            alt={modalImage.alt}
            onClose={handleCloseModal}
          />
        )}
      </>
    );
  }
); 
